import { useInfiniteQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { format, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ConnectionsService } from '../../../../../js/api';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { Drawer, DrawerBody } from '../../../components/drawer';
import { Heading } from '../../../components/heading';
import { ListFilter } from '../../../components/list-filter';
import { QueueList } from '../../../components/queue-list';
import { StatusPill } from '../../../components/StatusPill';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortHeader,
} from '../../../components/table';
import { QueueCodes, WorkflowScopes } from '../../../constants/workflows';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useListFilterParams } from '../../../hooks/useListFilterParams';
import { getDateTemporalityClass } from '../../../utils/date';
import { STICKY_HEADER } from '../../../utils/globalStyles';
import { MAIN_CONTENT_AREA, PAGE_HEADER } from '../../../utils/layoutStyles';
import { TRANSITION_ALL } from '../../../utils/transitionStyles';
import ClearanceCard from './ClearanceCard';
import { ClearanceDrawer } from './ClearanceDrawer';
import { ORGANIZATION_CLEARANCE_LIST_QUERY_KEY } from './queryKeys';

const DATE_FORMAT = 'LLL d, yyyy';
const PAGE_SIZE = 20; // Number of items per page

const QUEUE_CATEGORY = 'HealthcareEntityClearanceQueueNames';

// Map Code Def category names to their corresponding API parameter names
// This mapping must be kept in sync with the API method signature
// See ConnectionsService.connectionsClearanceList for available parameters
// Categories are automatically derived from this mapping's keys
type ApiParams = Parameters<typeof ConnectionsService.connectionsClearanceList>[0];
const CATEGORY_TO_API_PARAM: Record<string, keyof ApiParams> = {
  // Add more mappings here as needed:
  // ProviderRateFields: 'rates',
  // PostalStateChoices: 'state',
};

// Derive categories list from the mapping keys - ensures they stay in sync
const CATEGORIES = Object.keys(CATEGORY_TO_API_PARAM) as readonly string[];

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ClearanceList: React.FC = () => {
  useDocumentTitle('Clearances');
  const [searchParams, setSearchParams] = useSearchParams();

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Get the selected queue code from URL params
  // QueueList component handles determining initial tab and validation
  const selectedQueueCode: string | undefined = searchParams.get('queue') || undefined;

  // Use hook to parse and manage filter params
  // TODO: using a hook to centralize query param parsing logic to keep boilerplate out of parent
  // there is an argument for moving this logic to the ListFilter component itself,
  // but the parent component will still need the individual query param values for API calls
  const displayTypePrefKey = 'OrganizationClearance';
  const { filterValues, updateFilters } = useListFilterParams(searchParams, setSearchParams, {
    categories: CATEGORIES,
    displayTypePrefKey,
  });

  const search = filterValues.search || '';
  const statuses = filterValues.statuses || [];
  const reasons = filterValues.reasons || [];
  const categoryFilters = useMemo(() => filterValues.categories || {}, [filterValues.categories]);

  // Handle queue change - update URL param
  const handleQueueChange = (queueCode: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (queueCode) {
      newSearchParams.set('queue', queueCode);
    } else {
      newSearchParams.delete('queue');
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  // Get the queue code to pass to API
  // Validation is handled by the backend, invalid queue values will be rejected by django
  // Type assertion needed for TypeScript, but runtime validation happens on backend
  // Note: queue parameter may not be in TypeScript types yet - backend filter supports it
  const apiQueueCode = selectedQueueCode || undefined;

  // Display type is resolved by the hook (single source of truth)
  const displayType = (filterValues.displayType || 'table') as 'card' | 'table';
  const ordering = (filterValues.ordering || '-updated_at') as string;

  const setOrdering = useCallback(
    (next: string) => {
      updateFilters({
        search: filterValues.search,
        statuses: filterValues.statuses,
        reasons: filterValues.reasons,
        categories: filterValues.categories,
        displayType,
        ordering: next,
      });
    },
    [
      updateFilters,
      filterValues.search,
      filterValues.statuses,
      filterValues.reasons,
      filterValues.categories,
      displayType,
    ]
  );

  // Build API parameters from category filters using CATEGORY_TO_API_PARAM mapping
  const categoryApiParams = useMemo(() => {
    const params: Partial<ApiParams> = {};
    Object.entries(categoryFilters).forEach(([category, values]) => {
      const apiParamName = CATEGORY_TO_API_PARAM[category];
      if (apiParamName && values.length > 0) {
        // Type assertion needed because TypeScript can't infer types from dynamic property access
        // The mapping is validated at compile-time through CATEGORY_TO_API_PARAM's Record type
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (params as Record<string, unknown>)[apiParamName] = values;
      }
    });
    return params;
  }, [categoryFilters]);

  // Build query key with filters
  const queryKey = useMemo(
    () => [
      ORGANIZATION_CLEARANCE_LIST_QUERY_KEY,
      selectedQueueCode,
      search,
      statuses,
      reasons,
      categoryFilters,
      ordering,
    ],
    [selectedQueueCode, search, statuses, reasons, categoryFilters, ordering]
  );

  // Fetch clearances filtered by selected queue and filters
  const {
    data,
    isLoading: isLoadingClearances,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      // Note: queue parameter is supported by backend filter but may not be in TypeScript types
      // Using type assertion to pass it - backend will handle validation
      return ConnectionsService.connectionsClearanceList({
        // @ts-expect-error - queue parameter supported by backend but may not be in generated types
        queue: apiQueueCode,
        search: search || undefined,
        status: statuses.length > 0 ? (statuses as Array<string | null>) : undefined,
        reason: reasons.length > 0 ? (reasons as Array<string | null>) : undefined,
        ...categoryApiParams,
        ordering: ordering as ApiParams['ordering'],
        limit: PAGE_SIZE,
        offset: pageParam as number,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.next) {
        return allPages.length * PAGE_SIZE;
      }
      return undefined;
    },
    enabled: !!selectedQueueCode, // Only fetch when we have a queue selected
  });

  const clearances = useMemo(() => {
    return data?.pages.flatMap((page) => page.results || []) || [];
  }, [data]);

  const totalCount = data?.pages[0]?.count || 0;

  // Set up infinite scroll
  const loadMoreClearances = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { loadingRef } = useInfiniteScroll(loadMoreClearances, hasNextPage || false, {
    enabled: hasNextPage && !isLoadingClearances,
  });

  // Check URL for clearance ID and open drawer if present
  useEffect(() => {
    const clearanceIdFromUrl = searchParams.get('id');
    if (clearanceIdFromUrl) {
      setIsDrawerOpen(true);
    } else {
      if (isDrawerOpen) {
        setIsDrawerOpen(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleRowClick = useCallback(
    (clearanceId: string) => {
      setIsDrawerOpen(true);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('id', clearanceId);
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('id');
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Use URL param as single source of truth for selection to avoid state desync
  const selectedIdFromUrl = searchParams.get('id');

  // Determine index of selected item in the current results
  const selectedIndex = useMemo(() => {
    if (!selectedIdFromUrl) return -1;
    return clearances.findIndex((c) => String(c.id) === String(selectedIdFromUrl));
  }, [clearances, selectedIdFromUrl]);

  const prevClearanceId: string | null = useMemo(() => {
    if (selectedIndex > 0) return clearances[selectedIndex - 1]?.id ?? null;
    return null;
  }, [clearances, selectedIndex]);

  const nextClearanceId: string | null = useMemo(() => {
    if (selectedIndex >= 0 && selectedIndex < clearances.length - 1)
      return clearances[selectedIndex + 1]?.id ?? null;
    return null;
  }, [clearances, selectedIndex]);

  // Cache last-known neighbors so if the selected item drops from the list, we can still advance
  const neighborsByIdRef = useRef<Map<string, { prev: string | null; next: string | null }>>(
    new Map()
  );

  // Deep-link guard for initial `id` from URL: keep the initially deep-linked id stable
  // on first load so the drawer doesn't immediately close if the item isn't on the first page.
  const initialIdRef = useRef<string | null>(searchParams.get('id'));

  // Update neighbor cache whenever the selected item is present in the list
  useEffect(() => {
    if (!selectedIdFromUrl) return;
    if (selectedIndex >= 0) {
      neighborsByIdRef.current.set(String(selectedIdFromUrl), {
        prev: prevClearanceId ?? null,
        next: nextClearanceId ?? null,
      });
    }
  }, [selectedIdFromUrl, selectedIndex, prevClearanceId, nextClearanceId]);

  // Fallback auto-advance: if the selected id is no longer in the list for the current queue,
  // navigate to cached next/prev, or close if none.
  useEffect(() => {
    // Only auto-advance when a specific queue (not 'all') is selected
    if (!selectedQueueCode || selectedQueueCode === 'all') return;
    if (!selectedIdFromUrl) return;

    // Keep the deep-linked id stable on initial load
    if (initialIdRef.current && selectedIdFromUrl === initialIdRef.current) {
      return;
    }

    // When the list finishes (re)loading and no longer contains the selected item
    if (selectedIndex === -1 && !isLoadingClearances) {
      const cached = neighborsByIdRef.current.get(String(selectedIdFromUrl));
      const targetId = cached?.next ?? cached?.prev ?? null;
      const nextParams = new URLSearchParams(searchParams);
      if (targetId) {
        nextParams.set('id', String(targetId));
        setSearchParams(nextParams, { replace: true });
      } else {
        nextParams.delete('id');
        setSearchParams(nextParams, { replace: true });
      }
    }
  }, [
    selectedQueueCode,
    selectedIdFromUrl,
    selectedIndex,
    isLoadingClearances,
    searchParams,
    setSearchParams,
  ]);

  return (
    <>
      <div className="tw:flex tw:h-[calc(100vh-72px)] tw:overflow-hidden">
        <div
          className={clsx(
            `tw:flex tw:flex-col ${TRANSITION_ALL} tw:overflow-hidden`,
            isDrawerOpen ? 'tw:w-3/5 tw:lg:w-3/5' : 'tw:w-full'
          )}
        >
          <main className={MAIN_CONTENT_AREA}>
            <div className={STICKY_HEADER}>
              <div className={PAGE_HEADER}>
                <Heading level={1} className="tw:!text-brand-green-0">
                  <i className="fas fa-fw fa-check-double" />
                  Clearances
                </Heading>
              </div>

              <div className="">
                <QueueList
                  categoryName={QUEUE_CATEGORY}
                  notificationScope={WorkflowScopes.Clearance}
                  selectedQueue={selectedQueueCode || undefined}
                  priorityQueues={true}
                  priorityQueueList={[QueueCodes.READY_FOR_CLEARANCE]}
                  onQueueChange={handleQueueChange}
                />
              </div>
            </div>
            <div className="tw:mt-1">
              <ListFilter
                show_search={true}
                workflow_scope={WorkflowScopes.Clearance}
                categories={Array.from(CATEGORIES)}
                showStatus={true}
                showReason={true}
                showDisplayType={true}
                values={filterValues}
                onFilterChange={updateFilters}
                sortOptions={[
                  { label: 'Last Updated', value: 'updated_at' },
                  { label: 'Created', value: 'created_at' },
                  { label: 'Next Review On', value: 'next_review_on' },
                  { label: 'Provider', value: 'provider_display_name' },
                  { label: 'Status', value: 'current_status__title' },
                  { label: 'Reason', value: 'current_reason__title' },
                ]}
              />
            </div>

            <div className="tw:mt-6">
              {isLoadingClearances && clearances.length === 0 ? (
                <div className="tw:text-center tw:py-8">Loading clearances...</div>
              ) : clearances.length === 0 ? (
                <div className="tw:text-center tw:text-zinc-500 tw:py-8">
                  No clearances found
                  <div className="tw:text-zinc-500 tw:italic tw:text-sm">
                    Refine your search or check back later
                  </div>
                </div>
              ) : (
                <>
                  {/* Show total count when there are results */}
                  {clearances.length > 0 && (
                    <div className="tw:text-right tw:text-xs tw:text-zinc-500 tw:my-1 tw:mr-1">
                      Showing {clearances.length} of {totalCount} clearances
                    </div>
                  )}

                  {displayType === 'card' ? (
                    <div className="tw:@container">
                      <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:mt-2 tw:@min-[513px]:grid-cols-2 tw:@min-[793px]:grid-cols-3">
                        {clearances.map((clearance) => {
                          const idFromUrl = searchParams.get('id');
                          const isSelected = clearance.id === idFromUrl;
                          return (
                            <button
                              key={clearance.id}
                              type="button"
                              onClick={() => handleRowClick(clearance.id)}
                              className="tw:block tw:w-full tw:text-left"
                            >
                              <ClearanceCard clearance={clearance} isSelected={isSelected} />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <>
                      <Table striped dense>
                        <TableHead>
                          <TableSortHeader
                            sortKey="provider_display_name"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Provider
                          </TableSortHeader>
                          <TableSortHeader
                            sortKey="current_status__title"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Status
                          </TableSortHeader>
                          {selectedQueueCode === 'all' && (
                            <TableSortHeader
                              sortKey="current_reason__title"
                              ordering={ordering}
                              onOrderingChange={setOrdering}
                            >
                              Reason
                            </TableSortHeader>
                          )}
                          {selectedQueueCode === 'all' && (
                            <TableSortHeader
                              sortKey="next_review_on"
                              ordering={ordering}
                              onOrderingChange={setOrdering}
                            >
                              Next Review
                            </TableSortHeader>
                          )}
                          <TableSortHeader
                            sortKey="updated_at"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Last Updated
                          </TableSortHeader>
                          <TableSortHeader
                            sortKey="created_at"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Created Date
                          </TableSortHeader>
                        </TableHead>
                        <TableBody>
                          {/* eslint-disable-next-line sonarjs/cognitive-complexity */}
                          {clearances.map((clearance) => {
                            const idFromUrl = searchParams.get('id');
                            const isSelected = clearance.id === idFromUrl;
                            return (
                              <TableRow
                                key={clearance.id}
                                onClick={() => handleRowClick(clearance.id)}
                                className={clsx(isSelected ? 'tw:!bg-brand-green-2' : '')}
                              >
                                <TableCell>{clearance.provider.display_name}</TableCell>
                                <TableCell>
                                  <StatusPill
                                    scope={WorkflowScopes.Clearance}
                                    status={clearance.current_status}
                                  />
                                </TableCell>
                                {selectedQueueCode === 'all' && (
                                  <TableCell>{clearance.current_reason?.title || '--'}</TableCell>
                                )}
                                {selectedQueueCode === 'all' && (
                                  <TableCell>
                                    {clearance.next_review_on ? (
                                      <span
                                        className={getDateTemporalityClass(
                                          clearance.next_review_on
                                        )}
                                      >
                                        {format(parseISO(clearance.next_review_on), DATE_FORMAT)}
                                      </span>
                                    ) : (
                                      '--'
                                    )}
                                  </TableCell>
                                )}
                                <TableCell>
                                  {clearance.updated_at
                                    ? format(parseISO(clearance.updated_at), DATE_FORMAT)
                                    : '--'}
                                </TableCell>
                                <TableCell>
                                  {clearance.created_at
                                    ? format(parseISO(clearance.created_at), DATE_FORMAT)
                                    : '--'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </>
                  )}
                  {/* Infinite scroll loading indicator */}
                  {hasNextPage && (
                    <div ref={loadingRef} className="tw:flex tw:justify-center tw:py-8">
                      {isFetchingNextPage && (
                        <div className="tw:text-center tw:text-zinc-500">
                          Loading more clearances...
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </main>
        </div>

        {/* Drawer - side-by-side on desktop, modal on mobile */}
        <Drawer open={isDrawerOpen} onClose={handleCloseDrawer} width="3xl" mode="side-by-side">
          <DrawerBody className="tw:!text-sm/6">
            {selectedIdFromUrl && (
              <ClearanceDrawer
                key={selectedIdFromUrl}
                clearanceId={selectedIdFromUrl}
                prevId={(() => {
                  const cached = selectedIdFromUrl
                    ? neighborsByIdRef.current.get(String(selectedIdFromUrl))
                    : undefined;
                  return selectedIndex >= 0 ? prevClearanceId : (cached?.prev ?? null);
                })()}
                nextId={(() => {
                  const cached = selectedIdFromUrl
                    ? neighborsByIdRef.current.get(String(selectedIdFromUrl))
                    : undefined;
                  return selectedIndex >= 0 ? nextClearanceId : (cached?.next ?? null);
                })()}
                selectedQueueCode={selectedQueueCode}
                onClose={handleCloseDrawer}
              />
            )}
          </DrawerBody>
        </Drawer>
      </div>
    </>
  );
};
